import { useEffect, useState } from 'react';

import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Checkbox } from '@/shared/ui/checkbox';
import { toast } from '@/shared/ui/sonner';
import { majSousCompte } from '@/shared/api/superadmin';

/**
 * Modules souscrits et plafonds d'un sous-compte.
 *
 * Ces trois réglages ne sont pas décoratifs : décocher un module ferme réellement ses
 * routes, et un plafond atteint refuse la création suivante. Le serveur les applique
 * depuis le portage, mais aucun écran ne permettait de les régler — un verrou sans
 * serrure. C'est aussi ce que fait la version de référence, à la différence près que
 * chez elle la liste est stockée puis jamais relue.
 *
 * Un plafond vide vaut « pas de limite », et se distingue de zéro, qui interdirait tout.
 */
const MODULES = ['CRM', 'Monitoring', 'Pilotage', 'Revenus', 'Parametres', 'SAV', 'API_Partenaire', 'CFTSign'];

const LIBELLES = {
  CRM: 'CRM', Monitoring: 'Supervision', Pilotage: 'Pilotage', Revenus: 'Estimations et facturation',
  Parametres: 'Paramètres', SAV: 'SAV', API_Partenaire: 'API partenaire', CFTSign: 'Parcours de signature',
};

const nombreOuVide = (valeur) => (valeur === '' || valeur === null || valeur === undefined ? null : Number(valeur));

export default function ModulesEtPlafonds({ sousCompte, onEnregistre }) {
  const [modules, setModules] = useState([]);
  const [maxActifs, setMaxActifs] = useState('');
  const [maxComptes, setMaxComptes] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    setModules(sousCompte?.modules || []);
    setMaxActifs(sousCompte?.max_assets ?? '');
    setMaxComptes(sousCompte?.max_users ?? '');
  }, [sousCompte]);

  const basculer = (module) =>
    setModules((actuels) =>
      actuels.includes(module) ? actuels.filter((m) => m !== module) : [...actuels, module]);

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      await majSousCompte(sousCompte.id, {
        modules,
        max_assets: nombreOuVide(maxActifs),
        max_users: nombreOuVide(maxComptes),
      });
      toast.success('Modules et plafonds enregistrés');
      onEnregistre?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    }
    setEnregistrement(false);
  };

  return (
    <Card data-testid="modules-et-plafonds">
      <CardContent className="space-y-5 p-5">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Modules souscrits</p>
          <p className="mt-0.5 text-[12px] text-slate-500">
            Décocher un module ferme ses routes pour tout le sous-compte, quel que soit le rôle.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {MODULES.map((module) => (
              <label
                key={module}
                className="flex cursor-pointer items-center gap-2 rounded border border-slate-200 p-2 text-[12px] hover:bg-slate-50"
              >
                <Checkbox
                  checked={modules.includes(module)}
                  onCheckedChange={() => basculer(module)}
                  data-testid={`module-${module}`}
                />
                <span className="truncate">{LIBELLES[module] || module}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="max-actifs">Plafond d&apos;équipements</Label>
            <Input
              id="max-actifs" type="number" min="0" placeholder="Sans limite"
              value={maxActifs} onChange={(e) => setMaxActifs(e.target.value)}
              data-testid="max-actifs"
            />
          </div>
          <div>
            <Label htmlFor="max-comptes">Plafond de comptes</Label>
            <Input
              id="max-comptes" type="number" min="0" placeholder="Sans limite"
              value={maxComptes} onChange={(e) => setMaxComptes(e.target.value)}
              data-testid="max-comptes"
            />
          </div>
        </div>
        <p className="text-[11px] text-slate-400">
          Un champ vide signifie « sans limite ». Zéro interdirait toute création.
        </p>

        <Button onClick={enregistrer} disabled={enregistrement} size="sm" data-testid="enregistrer-modules">
          {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </CardContent>
    </Card>
  );
}
